import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { ArrowLeft, Building2 } from 'lucide-react';
import { BackToListRow, Button, CompactError, StepBar } from '../ui';
import { outreachThreadPath, wizardStepPath } from '../../utils/wizardRoutes';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useSupplierOutreach } from '../../hooks/useSupplierOutreach';
import { useReportRobbieUiContext } from '../../contexts/RobbieUiContext';
import { actionsForThread } from '../../hooks/outreachActionModel';
import { ThreadMessage } from './outreach/types';
import ThreadList from './outreach/ThreadList';
import ThreadChat from './outreach/ThreadChat';
import OutreachLoading from './outreach/OutreachLoading';
import { useOutreachContinue } from '../../hooks/useOutreachContinue';
import { useOutreachKickoffTimeout } from '../../hooks/useOutreachKickoffTimeout';
import { outreachStepSummary } from './outreach/outreachStepSummary';

/**
 * Wizard step 4 — Supplier Outreach in the v2 surface (`design/
 * step4-outreach.dc.html`): an action-sorted thread list beside the
 * conversation, with Robbie's next moves above the composer.
 *
 * There is ONE read of this step. The buyer lands in the conversations
 * whatever has or hasn't been sent — a supplier nobody has written to opens on
 * their opening asks in the same pane the reply would arrive in, so the flow
 * from suggestion to composer to send is the same one every time.
 *
 * The mock's state strip above the list is not rendered: its counts restated
 * what the thread list already says, row by row, more precisely.
 *
 * The active thread lives in the URL (`/rfqs/:rfqId/outreach/:threadId?`), so
 * refresh/share/back restore it. The buyer's input is always a QUERY — asking
 * is the approval, and Robbie writes and sends the email from it.
 *
 * On a phone the two panes become two screens, the way every messaging app on
 * the device already works: the step opens on the list of suppliers, tapping
 * one opens that conversation full width, and a back row returns to the list.
 * Side by side does not shrink to 375px, and stacked it is worse than either —
 * a list with somebody else's conversation underneath it, joined by nothing a
 * reader can see. Because the open thread is already the URL, the drill-down
 * IS navigation: the phone's own back gesture goes back to the list, and a
 * link to a conversation still opens that conversation.
 */
interface SupplierOutreachProps {
  rfq: any;
  /** May be async; a rejection is shown, never swallowed. */
  onNext: () => void | Promise<void>;
  onBack: () => void;
  /** When set, the forward button is disabled and this explains why. */
  continueDisabledReason?: string;
}

const SupplierOutreach: React.FC<SupplierOutreachProps> = ({ rfq, onNext, onBack, continueDisabledReason }) => {
  const rfqData = useSelector((state: RootState) => state.rfq.rfqData);
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  // NOT named `storedRfqId`: that is the shared helper, and a local of the
  // same name holding the STORED id invites someone to interpolate it into
  // a path directly, which is how the organization's code reaches an
  // address bar. The route builders apply the helper themselves.
  const storedRfqId = rfq?.rfqId || rfq?.id || '';

  const {
    threads,
    activeThread,
    actionIndex,
    kickoffPending,
    stageActions,
    stagedActionIds,
    dismissAction,
    messageInput,
    setMessageInput,
    isLoadingBOM,
    isSendingThread,
    retrySend,
    refreshActions,
    sendError,
    dismissSendError,
    handleSendMessage,
    removeUncontactedThread,
    isDiscardingThread,
    saveContactEmail,
    isSavingEmail,
    emailSaveError,
    decideSpec,
    isSavingSpecDecision,
    specDecisionError,
    redirectThreadId,
    clearRedirectThreadId,
  } = useSupplierOutreach(rfq, rfqData, threadId);

  // What the buyer is actually looking at. `activeThread` falls back to the
  // first thread so the desktop's right pane is never blank; on a phone
  // showing the list, nothing is open and the URL is the only answer.
  const openThread = !isMobile || threadId ? activeThread : null;

  // Robbie sees what the buyer sees: the open conversation — which supplier,
  // for which component, and where the thread stands.
  useReportRobbieUiContext(
    openThread
      ? {
          view: 'supplier-outreach',
          threadId: openThread.id,
          supplierName: openThread.supplierName,
          componentId: openThread.componentId,
          componentName: openThread.componentName,
          // The composer exists only here, so Robbie learns what is typed in it from the screen.
          detail: `thread status: ${openThread.status}${messageInput.trim() ? `; composer draft: "${messageInput.trim().slice(0, 400)}"` : '; composer empty'}`,
        }
      : { view: 'supplier-outreach' },
  );

  // Removing an un-started thread deletes it from the list — send the URL back
  // to the step root so the canonicalizer lands on the first remaining thread.
  const handleDiscardThread = async () => {
    if (!activeThread) return;
    const removed = await removeUncontactedThread(activeThread.id);
    if (removed && storedRfqId) {
      navigate(wizardStepPath(storedRfqId, { step: 4, subStep: 1 }), { replace: true });
    }
  };

  // Canonicalize the address bar: no/unknown thread id → the first thread.
  // A just-started thread is the exception — its id changes to the real
  // comms thread id on refresh, so follow it instead of bouncing to threads[0].
  useEffect(() => {
    if (!storedRfqId || threads.length === 0) return;
    if (redirectThreadId) {
      if (threads.some((t) => t.id === redirectThreadId)) {
        if (threadId !== redirectThreadId) {
          navigate(outreachThreadPath(storedRfqId, redirectThreadId), { replace: true });
        }
        clearRedirectThreadId();
      }
      // Until the refreshed BOM lands, hold position — no fallback bounce.
      return;
    }
    // A phone opens on the list, so a URL with no thread is a real state
    // there rather than one to canonicalize away.
    if (!threadId) {
      if (!isMobile) navigate(outreachThreadPath(storedRfqId, threads[0].id), { replace: true });
      return;
    }
    if (!threads.some((t) => t.id === threadId)) {
      // A thread that no longer exists sends a phone back to the list and a
      // desktop to the first thread — each to its own empty-handed state.
      navigate(
        isMobile ? wizardStepPath(storedRfqId, { step: 4, subStep: 1 }) : outreachThreadPath(storedRfqId, threads[0].id),
        { replace: true },
      );
    }
  }, [storedRfqId, threads, threadId, navigate, redirectThreadId, clearRedirectThreadId, isMobile]);

  const kickoff = useOutreachKickoffTimeout(kickoffPending, activeThread?.id ?? '');
  const retryKickoff = () => {
    kickoff.restart();
    refreshActions();
  };
  const { handleContinue, isContinuing, continueError, dismissContinueError } = useOutreachContinue(onNext);

  const activeActions = useMemo(
    () => (activeThread ? actionsForThread(actionIndex, activeThread) : []),
    [actionIndex, activeThread],
  );

  // Everything both layouts hand the open conversation, bar the thread itself.
  const chatProps = {
    actions: activeActions,
    kickoffPending,
    kickoffTimedOut: kickoff.timedOut,
    onRetryKickoff: retryKickoff,
    messageInput,
    onMessageInputChange: setMessageInput,
    onSendMessage: handleSendMessage,
    onStageActions: stageActions,
    stagedActionIds,
    onDismissAction: dismissAction,
    isSending: isSendingThread(openThread),
    onRetrySend: (message: ThreadMessage) => openThread && retrySend(openThread, message),
    sendError,
    onDismissSendError: dismissSendError,
    onSaveContactEmail: saveContactEmail,
    isSavingEmail,
    emailSaveError,
    onDecideSpec: decideSpec,
    isSavingSpecDecision,
    specDecisionError,
    onDiscardThread: handleDiscardThread,
    isDiscardingThread,
  };

  return (
    <div className="flex flex-col gap-s4 font-sans">
      {isLoadingBOM ? (
        <OutreachLoading />
      ) : threads.length === 0 || !activeThread ? (
        <div className="flex min-h-[440px] flex-col items-center justify-center gap-s4 rounded-card border border-line bg-surface p-[24px] text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-card bg-neutral-bg">
            <Building2 className="h-6 w-6 text-ink-muted" />
          </span>
          <div>
            <p className="m-0 text-row-title text-ink">No shortlisted suppliers yet</p>
            <p className="m-0 mt-s1 max-w-md text-body-sm text-ink-secondary">
              Outreach threads are created from your Supplier Discovery shortlist. Go back and pick the suppliers to contact.
            </p>
          </div>
          <Button variant="secondary" iconLeft={<ArrowLeft />} onClick={onBack}>
            Back to Supplier Discovery
          </Button>
        </div>
      ) : isMobile && !openThread ? (
        // Screen one of two: the suppliers, and nothing else competing for
        // the width. Tapping a row is navigation, so the phone's back gesture
        // comes back here from the conversation.
        <div className="overflow-hidden rounded-card border border-line bg-surface">
          <ThreadList
            threads={threads}
            actionIndex={actionIndex}
            onSelectThread={(id) => navigate(outreachThreadPath(storedRfqId, id))}
          />
        </div>
      ) : isMobile && openThread ? (
        // Screen two: one conversation, full width, with the way back on top
        // of it. ThreadChat draws its own supplier header underneath, so this
        // row stays a back control and says nothing twice.
        <div className="flex flex-col overflow-hidden rounded-card border border-line bg-surface">
          <BackToListRow
            label="All threads"
            count={threads.length}
            onBack={() => navigate(wizardStepPath(storedRfqId, { step: 4, subStep: 1 }))}
          />
          <ThreadChat {...chatProps} activeThread={openThread} />
        </div>
      ) : (
        <div className="grid h-[calc(100vh-330px)] min-h-[440px] grid-cols-[minmax(150px,268px)_minmax(0,1fr)] overflow-hidden rounded-card border border-line bg-surface">
          <ThreadList
            threads={threads}
            activeThreadId={activeThread.id}
            actionIndex={actionIndex}
            onSelectThread={(id) => navigate(outreachThreadPath(storedRfqId, id))}
          />
          <ThreadChat {...chatProps} activeThread={activeThread} />
        </div>
      )}

      {continueError && (
        <CompactError
          layout="row"
          title="I couldn’t open quotes yet"
          message={continueError}
          action={{ label: 'Try again', onClick: handleContinue }}
          secondaryAction={{ label: 'Dismiss', onClick: dismissContinueError }}
        />
      )}

      <StepBar
        stepLabel="Step 4 · Outreach"
        summary={outreachStepSummary(threads)}
        onBack={onBack}
        cta={{
          label: 'Continue to quotes',
          onClick: handleContinue,
          disabled: Boolean(continueDisabledReason),
          loading: isContinuing,
          loadingLabel: 'Opening quotes…',
        }}
        ctaNote={continueDisabledReason}
      />
    </div>
  );
};

export default SupplierOutreach;
